<template>
  <div class="interaction-history">
    <div v-if="loading" class="interaction-state">
      <v-progress-circular indeterminate color="primary" :size="42" :width="4" />
      <span>Interaktionen werden geladen</span>
    </div>

    <div v-else-if="!interactions.length" class="interaction-state">
      <v-icon size="34">mdi-message-text-outline</v-icon>
      <strong>Keine Interaktionen gefunden</strong>
      <span>Für diese Person wurden noch keine separaten Kontakte dokumentiert.</span>
    </div>

    <div v-else class="interaction-list">
      <article v-for="(customerInteraction, index) in interactions" :key="interactionKey(customerInteraction, index)" class="interaction-entry">
        <div class="interaction-entry__date">
          <span>{{ $moment(customerInteraction.interactions.date).format("DD") }}</span>
          <strong>{{ $moment(customerInteraction.interactions.date).format("MMM YY") }}</strong>
        </div>
        <div class="interaction-entry__icon">
          <v-icon>mdi-message-text-clock-outline</v-icon>
        </div>
        <div class="interaction-entry__main">
          <div class="interaction-entry__heading">
            <strong>{{ customerInteraction.interactions.reason || "Interaktion" }}</strong>
            <span v-if="customerInteraction.interactions.location">
              <v-icon x-small>mdi-map-marker-outline</v-icon>
              {{ customerInteraction.interactions.location }}
            </span>
          </div>
          <p v-if="customerInteraction.interactions.notes">{{ customerInteraction.interactions.notes }}</p>
          <p v-else class="interaction-entry__empty">Keine Notiz hinterlegt.</p>
        </div>
        <div class="interaction-entry__employee">
          <v-avatar size="34" color="#27312b">
            <img
              v-if="employeeImage(customerInteraction.employee)"
              :src="employeeImage(customerInteraction.employee)"
              :alt="employeeName(customerInteraction.employee)" />
            <span v-else>{{ employeeInitials(customerInteraction.employee) }}</span>
          </v-avatar>
          <span>{{ employeeName(customerInteraction.employee) || "Unbekannt" }}</span>
        </div>
      </article>
    </div>
  </div>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"

export default {
  props: {
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      userService,
      interactions: [],
      loading: false,
    }
  },
  async created() {
    this.loading = true
    try {
      this.interactions = await api.fetchCustomerInteractions(this.user._id)
    } catch (error) {
      this.$toast.error("Fehler beim Laden der Interaktionen")
    } finally {
      this.loading = false
      this.$emit("loaded")
    }
  },
  methods: {
    interactionKey(entry, index) {
      return entry?.interactions?._id || `${entry?.employee?._id || "employee"}-${entry?.interactions?.date || index}`
    },
    employeeName(employee) {
      if (!employee) return ""
      return `${employee.vorname || ""} ${employee.nachname || ""}`.trim()
    },
    employeeInitials(employee) {
      return `${employee?.vorname?.charAt(0) || ""}${employee?.nachname?.charAt(0) || ""}`.toUpperCase() || "?"
    },
    employeeImage(employee) {
      if (!employee) return ""
      if (employee.bild) return `/img/profilbilder${employee.bild}`
      if (employee._id) return `https://app.wellcomefit.ch/api/checkin/pool/images/${employee._id}`
      return ""
    },
  },
}
</script>

<style scoped>
.interaction-history {
  width: 100%;
  padding: 18px 24px 0;
}

.interaction-state {
  display: flex;
  min-height: 280px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #9fa5a1;
  text-align: center;
}

.interaction-state strong {
  color: #ffffff;
  font-size: 16px;
}

.interaction-state span {
  font-size: 12px;
}

.interaction-list {
  display: grid;
  gap: 9px;
}

.interaction-entry {
  display: grid;
  min-height: 88px;
  align-items: center;
  gap: 13px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.025);
  grid-template-columns: 58px 38px minmax(0, 1fr) auto;
}

.interaction-entry__date {
  display: flex;
  flex-direction: column;
  text-align: center;
}

.interaction-entry__date span {
  color: #ffffff;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
}

.interaction-entry__date strong {
  margin-top: 3px;
  color: #8e9690;
  font-size: 12px;
  text-transform: uppercase;
}

.interaction-entry__icon {
  display: inline-flex;
  width: 38px;
  height: 38px;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #d8b4fe;
  background: rgba(168, 85, 247, 0.12);
}

.interaction-entry__icon .v-icon {
  color: inherit;
}

.interaction-entry__main {
  min-width: 0;
}

.interaction-entry__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 7px 13px;
}

.interaction-entry__heading strong {
  color: #f5f7f6;
  font-size: 14px;
}

.interaction-entry__heading span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #9da39f;
  font-size: 12px;
}

.interaction-entry__main p {
  margin: 5px 0 0;
  color: #bdc2be;
  font-size: 12px;
  line-height: 1.45;
}

.interaction-entry__empty {
  color: #777f79 !important;
  font-style: italic;
}

.interaction-entry__employee {
  display: flex;
  max-width: 190px;
  align-items: center;
  gap: 8px;
}

.interaction-entry__employee > span {
  overflow: hidden;
  color: #b7bcb8;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 700px) {
  .interaction-history {
    padding: 14px 16px 0;
  }

  .interaction-entry {
    align-items: flex-start;
    grid-template-columns: 48px minmax(0, 1fr);
  }

  .interaction-entry__icon {
    display: none;
  }

  .interaction-entry__main {
    grid-column: 2;
  }

  .interaction-entry__employee {
    max-width: none;
    grid-column: 2;
  }
}
</style>
